import { ControlPosition, MapControl, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import React, { useEffect, useRef, useState } from "react";
import { POLYGON_OPTIONS } from "./styles";
import { Animate } from "react-simple-animate";
const getVertices = (polygon) => polygon
    .getPath().getArray().map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
const DrawingManager = ({ area, onChange }) => {
    const [polygonVertices, setPolygonVertices] = useState([]);
    const [drawingManager, setDrawingManager] = useState(null);
    const polygonRef = useRef(null); // Track the current polygon
    const drawing = useMapsLibrary('drawing');
    const map = useMap();
    // Initialize the DrawingManager
    useEffect(() => {
        if (!drawing)
            return;
        const newDrawingManager = new drawing.DrawingManager({
            drawingMode: google.maps.drawing.OverlayType.POLYGON,
            drawingControl: false,
            polygonOptions: Object.assign({ editable: true }, POLYGON_OPTIONS),
        });
        setDrawingManager(newDrawingManager);
        // Clean up the DrawingManager
        return () => {
            newDrawingManager.setMap(null);
        };
    }, [drawing]);
    // Attach the DrawingManager to the map and set up event listeners
    useEffect(() => {
        if (!drawingManager)
            return;
        // Attach the DrawingManager to the map
        drawingManager.setMap(map);
        //If an area is passed in, draw it
        if (area)
            createPolygon(area);
        // Listen for polygon completion
        google.maps.event.addListener(drawingManager, 'polygoncomplete', (polygon) => {
            // Clear any existing polygon
            if (polygonRef.current) {
                polygonRef.current.setMap(null); // Remove the existing polygon from the map
            }
            // Save the new polygon reference
            polygonRef.current = polygon;
            setPolygonVertices(getVertices(polygon)); // Save the vertices to state
            // Make the polygon editable and draggable
            polygon.setEditable(true);
            polygon.setDraggable(true);
            // Listen for edits to the polygon
            polygon.getPath().addListener('set_at', () => setPolygonVertices(getVertices(polygon)));
            polygon.getPath().addListener('insert_at', () => setPolygonVertices(getVertices(polygon)));
            polygon.getPath().addListener('remove_at', () => setPolygonVertices(getVertices(polygon)));
            // Exit drawing mode after the polygon is completed
            drawingManager.setDrawingMode(null);
        });
    }, [drawingManager]);
    //Update form value when polygonVertices updates
    useEffect(() => {
        onChange(polygonVertices);
    }, [polygonVertices]);
    // Handle the "Add Polygon" button click
    const handleAddPolygonClick = () => {
        if (!drawingManager)
            return;
        // Clear any existing polygon
        if (polygonRef.current) {
            polygonRef.current.setMap(null); // Remove the existing polygon from the map
            polygonRef.current = null; // Reset the polygon reference
        }
        // Clear any existing polygon
        setPolygonVertices(null);
        // Start drawing a new polygon
        drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    };
    const createPolygon = (area) => {
        if (!drawingManager)
            return;
        // Attach the DrawingManager to the map
        drawingManager.setMap(map);
        // Clear any existing polygon
        if (polygonRef.current) {
            polygonRef.current.setMap(null); // Remove the existing polygon from the map
        }
        // Save the new polygon reference
        const polygon = new google.maps.Polygon(Object.assign(Object.assign({}, POLYGON_OPTIONS), { paths: area }));
        polygonRef.current = polygon;
        setPolygonVertices(getVertices(polygon));
        // Make the polygon editable and draggable
        polygon.setEditable(true);
        polygon.setDraggable(true);
        // Listen for edits to the polygon
        polygon.getPath().addListener('set_at', () => setPolygonVertices(getVertices(polygon)));
        polygon.getPath().addListener('insert_at', () => setPolygonVertices(getVertices(polygon)));
        polygon.getPath().addListener('remove_at', () => setPolygonVertices(getVertices(polygon)));
        polygon.setMap(map);
        // Exit drawing mode after the polygon is completed
        drawingManager.setDrawingMode(null);
    };
    return (React.createElement(MapControl, { position: ControlPosition.LEFT_BOTTOM },
        React.createElement(Animate, { play: !!polygonVertices, start: { opacity: 0 }, end: { opacity: 1 }, duration: !!polygonVertices ? .4 : 0, easeType: "ease-in" },
            React.createElement("button", { type: "button", className: "btn btn-blue-reverse btn-icon btn-draw", onClick: handleAddPolygonClick },
                React.createElement("i", { className: "fa-regular fa-draw-polygon" }),
                " Redraw Area"))));
};
export default DrawingManager;
